import React, { useEffect, useState } from "react";
import { Link, withRouter, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { fireConfetti } from "@utils/confetti";
import logo from "@assets/images/logo/dark-logo-text-nobg.png";

const SubscriptionSuccess = () => {
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Fire confetti on mount
    fireConfetti();
    // Trigger mount animation
    setTimeout(() => setMounted(true), 100);

    // Fire confetti again after a short delay for extra celebration
    const timer = setTimeout(() => fireConfetti(), 1500);
    return () => clearTimeout(timer);
  }, []);

  // Get session_id from URL if available (for future use to fetch session details)
  const searchParams = new URLSearchParams(location.search);
  const sessionId = searchParams.get("session_id");

  // Build dashboard URL with workspace
  const dashboardUrl = user?.workspaceId
    ? `/a/ws/${user.workspaceId}/dashboard`
    : "/a/dashboard";

  const styles = {
    container: {
      minHeight: "100vh",
      background: "#ffffff",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "20px",
      position: "relative",
      overflow: "hidden",
    },
    card: {
      background: "#ffffff",
      border: "1px solid #e5e7eb",
      borderRadius: "20px",
      padding: "32px",
      maxWidth: "420px",
      width: "100%",
      textAlign: "center",
      position: "relative",
      zIndex: 1,
      transform: mounted ? "translateY(0)" : "translateY(30px)",
      opacity: mounted ? 1 : 0,
      transition: "all 0.6s cubic-bezier(0.16, 1, 0.3, 1)",
      boxShadow: "0 4px 24px rgba(0, 0, 0, 0.08)",
    },
    logoContainer: {
      marginBottom: "16px",
    },
    logo: {
      height: "40px",
      objectFit: "contain",
    },
    successIcon: {
      width: "64px",
      height: "64px",
      margin: "0 auto 16px",
      background: "linear-gradient(135deg, rgba(52, 211, 153, 0.2) 0%, rgba(16, 185, 129, 0.1) 100%)",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      position: "relative",
      animation: mounted ? "pulse 2s ease-in-out infinite" : "none",
    },
    checkmark: {
      width: "32px",
      height: "32px",
      color: "#34D399",
    },
    successRing: {
      position: "absolute",
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      border: "2px solid rgba(52, 211, 153, 0.3)",
      animation: "ring 2s ease-out infinite",
    },
    title: {
      fontSize: "24px",
      fontWeight: "700",
      color: "#1f2937",
      marginBottom: "8px",
      letterSpacing: "-0.5px",
    },
    subtitle: {
      fontSize: "14px",
      color: "#6b7280",
      marginBottom: "20px",
      lineHeight: "1.5",
    },
    planInfo: {
      background: "#f3f0ff",
      border: "1px solid #e0d6ff",
      borderRadius: "12px",
      padding: "14px",
      marginBottom: "20px",
    },
    planLabel: {
      fontSize: "10px",
      color: "#6b7280",
      textTransform: "uppercase",
      letterSpacing: "1px",
      marginBottom: "4px",
    },
    planName: {
      fontSize: "20px",
      fontWeight: "600",
      color: "#7B61FF",
      marginBottom: "2px",
    },
    planStatus: {
      fontSize: "12px",
      color: "#34D399",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "6px",
    },
    buttonPrimary: {
      width: "100%",
      padding: "14px 24px",
      fontSize: "14px",
      fontWeight: "600",
      color: "#ffffff",
      background: "linear-gradient(135deg, #7B61FF 0%, #A78BFA 100%)",
      border: "none",
      borderRadius: "10px",
      cursor: "pointer",
      transition: "all 0.3s ease",
      marginBottom: "10px",
      textDecoration: "none",
      display: "block",
    },
    buttonSecondary: {
      width: "100%",
      padding: "12px 24px",
      fontSize: "13px",
      fontWeight: "500",
      color: "#6b7280",
      background: "transparent",
      border: "1px solid #e5e7eb",
      borderRadius: "10px",
      cursor: "pointer",
      transition: "all 0.3s ease",
      textDecoration: "none",
      display: "block",
    },
    footer: {
      marginTop: "24px",
      color: "#9ca3af",
      fontSize: "11px",
    },
    footerLinks: {
      display: "flex",
      gap: "16px",
      justifyContent: "center",
      marginTop: "8px",
    },
    footerLink: {
      color: "#6b7280",
      textDecoration: "none",
      fontSize: "11px",
      transition: "color 0.2s ease",
    },
  };

  const CheckIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" style={styles.checkmark}>
      <path
        d="M9 12.75L11.25 15L15 9.75M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const ActiveDot = () => (
    <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#34D399", display: "inline-block" }} />
  );

  return (
    <div style={styles.container}>
      {/* Main card */}
      <div style={styles.card}>
        {/* Logo */}
        <div style={styles.logoContainer}>
          <img src={logo} alt="Copilotly" style={styles.logo} />
        </div>

        {/* Success icon with animation */}
        <div style={styles.successIcon}>
          <div style={styles.successRing} />
          <CheckIcon />
        </div>

        {/* Title */}
        <h1 style={styles.title}>Payment Successful!</h1>
        <p style={styles.subtitle}>
          Thank you for subscribing to Copilotly. Your account has been upgraded and you now have access to all premium features.
        </p>

        {/* Plan info */}
        <div style={styles.planInfo}>
          <div style={styles.planLabel}>Your Plan</div>
          <div style={styles.planName}>{user?.currentPlan || "Premium"}</div>
          <div style={styles.planStatus}>
            <ActiveDot />
            Active
          </div>
        </div>

        {/* CTA Buttons */}
        <Link to={dashboardUrl} style={styles.buttonPrimary}>
          Go to Dashboard
        </Link>
        <Link to="/profile/subscription" style={styles.buttonSecondary}>
          View Subscription Details
        </Link>
      </div>

      {/* Footer */}
      <div style={styles.footer}>
        <p>A confirmation email has been sent to your inbox.</p>
        <div style={styles.footerLinks}>
          <a href="mailto:support@copilotly.com" style={styles.footerLink}>Need help?</a>
          <a href="https://copilotly.com/terms" target="_blank" rel="noopener noreferrer" style={styles.footerLink}>Terms</a>
          <a href="https://copilotly.com/privacy" target="_blank" rel="noopener noreferrer" style={styles.footerLink}>Privacy</a>
        </div>
      </div>

      {/* Keyframe animations */}
      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translate(0, 0); }
            50% { transform: translate(-20px, 20px); }
          }
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
          @keyframes ring {
            0% { transform: scale(1); opacity: 1; }
            100% { transform: scale(1.5); opacity: 0; }
          }
        `}
      </style>
    </div>
  );
};

export default withRouter(SubscriptionSuccess);
